// src/pages/agency/AgencyRegistration.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AgencyHeader from '../../components/headers/AgencyHeader';
import AgencyRegistrationPop from '../../components/popups/AgencyRegistrationPop';
import AgencyInfoTab from '../../tabs/agencyRegistration/AgencyInfoTab';
import AgencySolicitorsTab from '../../tabs/agencyRegistration/AgencySolicitorsTab';
import { createAgency } from '../../services/api/AgencyService';
import { fetchDocumentTypes } from '../../services/api/DocumentTypeService';
import { createSolicitor, importSolicitorsFromCSV, exportSolicitorsToCSV } from '../../services/api/SolicitorService';
import { validateAgencyForm, displayAgencyValidationErrorsWithDialog } from '../../utils/agencyValidation';
import { validateSolicitorForm, displayValidationErrorsWithDialog } from '../../utils/solicitorValidation';
import { useErrorDialog, showSuccessDialog } from '../../providers/ErrorDialogProvider';
import '../../styles/Tabs.css';
import '../../styles/AgencyRegistration.css';
import iconTitle from '../../assets/img/icon-square.svg';
import iconBtnUl from '../../assets/img/icon-ul.svg';
import iconBtnDl from '../../assets/img/icon-dl.svg';
import { formatDateForApi } from '../../utils/dateFormatter';
import { buildDocumentTypesForApi, getDocumentTypeSelectionKey, normalizeDocumentTypes } from '../../utils/documentTypeUtils';

// 代理店登録ページコンポーネント
// ※pages/adminにも同様のコンポーネントがあるため、修正時は同時に確認すること
const AgencyRegistration = () => {
  const { agencyId } = useParams();
  const navigate = useNavigate();
  const { showError } = useErrorDialog();
  const [agencyData, setAgencyData] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    tradingCode: '',
    name: '',
    nameKana: '',
    contractDate: '',
    cancellationDate: '',
    postalCode: '',
    prefecture: '',
    city: '',
    building: '',
    phoneNumber: '',
    accountname: '',
    documentTypeIds: [],
  });
  const [documentTypes, setDocumentTypes] = useState([]);
  const [solicitorForm, setSolicitorForm] = useState({
    branchName: '',
    name: '',
    nameKana: '',
    startDate: '',
    endDate: '',
    profile: '',
    accountname: '',
  });

  // 新規登録用の初期データ設定（代理店名のみ）
  const initializeAgencyData = () => {
    setAgencyData({ name: '新規登録' });
  };

  // 登録確認ポップアップの表示状態を管理
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);

  const hasAgencyId = !!agencyId;

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      initializeAgencyData();
      if (hasAgencyId) {
        setActiveTab('solicitors');
      }
      setIsLoading(false);
    };
    fetchData();
  }, [hasAgencyId]);

  useEffect(() => {
    const fetchDocTypes = async () => {
      try {
        const response = await fetchDocumentTypes();
        const normalizedTypes = normalizeDocumentTypes(response);
        setDocumentTypes(normalizedTypes);
        setFormData((prev) => {
          if (prev.documentTypeIds?.length) {
            return prev;
          }
          const defaultDocumentTypeKeys = normalizedTypes
             .filter((type) => type?.insurance_type != null && type?.code)
             .map((type) => getDocumentTypeSelectionKey(type));
          return {
            ...prev,
            documentTypeIds: defaultDocumentTypeKeys
          };
        });
      } catch (error) {
        // エラーダイアログ表示はBaseApiServiceで行う
        // エラーコンソール出力は各ApiServiceで行う
      }
    };

    fetchDocTypes();
  }, []);

  // ローディング表示
  if (isLoading) {
    return <div>Loading...</div>;
  }

  // フォーム入力ハンドラー
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (activeTab === 'info') {
      setFormData(prev => ({ ...prev, [name]: value }));
    } else if (activeTab === 'solicitors') {
      setSolicitorForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleDocumentTypeToggle = (documentTypeId) => {
    setFormData((prev) => {
      const current = new Set(prev.documentTypeIds || []);
      if (current.has(documentTypeId)) {
        current.delete(documentTypeId);
      } else {
        current.add(documentTypeId);
      }
      return {
        ...prev,
        documentTypeIds: Array.from(current)
      };
    });
  };

  // 登録ボタンクリック時：バリデーション後にポップアップ表示
  const handleRegister = () => {
    if (activeTab === 'info') {
      const validationResult = validateAgencyForm(formData);
      if (!displayAgencyValidationErrorsWithDialog(validationResult, showError)) {
        return;
      }
    } else if (activeTab === 'solicitors') {
      const validationResult = validateSolicitorForm(solicitorForm);
      if (!displayValidationErrorsWithDialog(validationResult, showError)) {
        return;
      }
    }
    setShowConfirmationPopup(true);
  };

  // 確認ポップアップからの登録実行
  const confirmRegistration = async () => {
    setIsLoading(true);
    let newAgencyId = null;
    try {
      if (activeTab === 'info') {
        // 代理店登録用ペイロード
        const payload = {
          agency_name: formData.name,
          agency_name_kana: formData.nameKana,
          trading_code: formData.tradingCode,
          contract_date: formData.contractDate || null,
          cancellation_date: formData.cancellationDate || null,
          postal_code: formData.postalCode,
          prefecture: formData.prefecture,
          city: formData.city,
          building: formData.building || null,
          phone: formData.phoneNumber,
          accountname: formData.accountname,
          document_types: buildDocumentTypesForApi(formData.documentTypeIds, documentTypes),
        };
        const res = await createAgency(payload);
        newAgencyId = res.id;
        
        // 成功メッセージを表示
        showSuccessDialog('代理店の登録が完了しました。');
      } else {
        // 募集人登録前に再バリデーション
        const validationResult = validateSolicitorForm(solicitorForm);
        if (!displayValidationErrorsWithDialog(validationResult, showError)) {
          setIsLoading(false);
          return;
        }
        
        // 募集人登録用ペイロード
        const payload = {
          branch_name: solicitorForm.branchName || null,
          solicitor_name: solicitorForm.name,
          solicitor_name_kana: solicitorForm.nameKana,
          agency_id: parseInt(agencyId, 10),
          accountname: solicitorForm.accountname,
          start_date: solicitorForm.startDate ? formatDateForApi(solicitorForm.startDate) : null,
          end_date: solicitorForm.endDate ? formatDateForApi(solicitorForm.endDate) : null,
          profile: solicitorForm.profile || null,
        };
        await createSolicitor(payload);
        
        // 成功メッセージを表示
        showSuccessDialog('募集人の登録が完了しました。');
        
        // フォームをリセット
        setSolicitorForm({
          branchName: '',
          name: '',
          nameKana: '',
          startDate: '',
          endDate: '',
          profile: '',
          accountname: '',
        });
      }
      
      // 代理店登録時は詳細ページへ遷移
      if (newAgencyId) {
        navigate(`/agency/detail/${newAgencyId}`);
      }
    } catch (err) {
      // エラーダイアログ表示はBaseApiServiceで行う
      // エラーコンソール出力は各ApiServiceで行う
    } finally {
      setIsLoading(false);
      setShowConfirmationPopup(false);
    }
  };

  //CSVアップロード処理
  const handleCsvUpload = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv';
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      setIsLoading(true);
      try {
        await importSolicitorsFromCSV(agencyId, file);
        showSuccessDialog('CSVアップロードが完了しました。');
      } catch (err) {
        // エラーダイアログ表示はBaseApiServiceで行う
        // エラーコンソール出力は各ApiServiceで行う
      } finally {
        setIsLoading(false);
      }
    };
    fileInput.click();
  };

  // CSVダウンロード処理
  const handleCsvDownload = async () => {
    setIsLoading(true);
    try {
      await exportSolicitorsToCSV(agencyId);
    } catch (err) {
      // エラーダイアログ表示はBaseApiServiceで行う
      // エラーコンソール出力は各ApiServiceで行う
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AgencyHeader />
      <div className="page-container">
        <div className="content-wrapper">
          <div className="page-header">
            <div className="title-section">
              <div className="icon-square">
                <img src={iconTitle} alt="タイトルアイコン" />
              </div>
              <h1>代理店登録：{agencyData.name}</h1>
            </div>
            {/* 募集人タブが選択されている時のみCSVボタン表示 */}
            {activeTab === 'solicitors' && (
              <div className="solicitor-add-button-box">
                <button
                  className="solicitor-add-button button-small solicitor-add-button--pale-blue solicitor-add-button--img-none"
                  onClick={handleCsvUpload}
                  disabled={isLoading}
                >
                  <span>CSVアップロード</span>
                  <img
                    src={iconBtnUl}
                    alt="ボタンアイコン"
                    className="solicitor-icon-img"
                  />
                </button>
                <button
                  className="solicitor-add-button button-small solicitor-add-button--img-none solicitor-add-button--img-none"
                  onClick={handleCsvDownload}
                  disabled={isLoading}
                >
                  <span>CSVダウンロード</span>
                  <img
                    src={iconBtnDl}
                    alt="ボタンアイコン"
                    className="solicitor-icon-img"
                  />
                </button>
              </div>
            )}
          </div>
          <div className="tab-container">
            <div className="tab-menu">
              <button
                className={`tab-button ${activeTab === 'info' ? 'active' : ''} ${hasAgencyId ? 'disabled' : ''}`}
                onClick={() => !hasAgencyId && setActiveTab('info')}
                disabled={hasAgencyId}
              >
                代理店情報
              </button>
              <button className="tab-button disabled" disabled>
                登録保険会社
              </button>
              <button
                className={`tab-button ${activeTab === 'solicitors' ? 'active' : ''} ${!hasAgencyId ? 'disabled' : ''}`}
                onClick={() => hasAgencyId && setActiveTab('solicitors')}
                disabled={!hasAgencyId}
              >
                募集人
              </button>
            </div>
            <div className="tab-content">
              {!hasAgencyId && activeTab === 'info' && (
                <AgencyInfoTab
                  formData={formData}
                  handleChange={handleChange}
                  navigate={navigate}
                  handleRegister={handleRegister}
                  documentTypes={documentTypes}
                  selectedDocumentTypeIds={formData.documentTypeIds}
                  onDocumentTypeToggle={handleDocumentTypeToggle}
                />
              )}
              {hasAgencyId && activeTab === 'solicitors' && (
                <AgencySolicitorsTab
                  navigate={navigate}
                  solicitorForm={solicitorForm}
                  handleChange={handleChange}
                  handleRegister={handleRegister}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <AgencyRegistrationPop
        showConfirmationPopup={showConfirmationPopup}
        formData={activeTab === 'solicitors' ? solicitorForm : formData}
        closeConfirmationPopup={() => setShowConfirmationPopup(false)}
        confirmRegistration={confirmRegistration}
        activeTab={activeTab}
        documentTypes={documentTypes}
      />
    </>
  );
};

export default AgencyRegistration;
